import type { KeyboardEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

interface UseInlineEditableTextParams {
  value: string
  isEditing: boolean
  isLoading: boolean
  onSave: (newValue: string) => void
  onCancel: () => void
}

const focusAndSelectInput = (input: HTMLInputElement | null): void => {
  requestAnimationFrame(() => {
    input?.focus()
    input?.select()
  })
}

export const useInlineEditableText = (params: UseInlineEditableTextParams) => {
  const { value, isEditing, isLoading, onSave, onCancel } = params
  const [draftValue, setDraftValue] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) return
    setDraftValue(value)
    focusAndSelectInput(inputRef.current)
  }, [isEditing, value])

  const handleSave = useCallback((): void => {
    const trimmed = draftValue.trim()
    if (trimmed && trimmed !== value) {
      onSave(trimmed)
      return
    }
    onCancel()
  }, [draftValue, value, onSave, onCancel])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSave()
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    },
    [handleSave, onCancel]
  )

  const handleBlur = useCallback((): void => {
    if (!isLoading) handleSave()
  }, [isLoading, handleSave])

  return { inputRef, draftValue, setDraftValue, handleKeyDown, handleBlur }
}
